
import { faUser, faUserTie, faEdit, faLink, faEnvelope, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import SoftBadge from 'components/common/SoftBadge';
import ModalGeneratingPDF from 'components/common/ModalGeneratingPDF';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, ButtonGroup, Card, Col, Dropdown, DropdownButton, Form, Row, Spinner, ToggleButton } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { Link, useNavigate } from 'react-router-dom';
import useOrdenComentarios from '../hooks/useOrdenComentarios';
import useOrdenOperations from '../hooks/useOrdenOperations';
import FacturaDropdown from './FacturaDropdown';
import OrdenFormaPago from './OrdenFormaPago';
import { useToast } from 'context/providers/ToastProvider';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import ModalCambiarVendedor from './ModalCambiarVendedor';
import ModalVincularXmlEmitido from './ModalVincularXmlEmitido';
import ModalDetalleSuscripcion from 'akasia-components/suscripciones/Modales/ModalDetalleSuscripcion';

const SeccionHeader = ({
    loadingInfoDetallesOrden,
    orden,
    setOrden,
    loadingEdit,
    verificaSiOrdenTienePagos,
    seleccionarFormaPago,
    setSeleccionarFormaPago,
    nuevaFormaPago,
    setNuevaFormaPago,
    seleccionarFecha,
    setSeleccionarFecha,
    onComentarioAgregado
}) => {
    const { cambiarFecha: cambiarFechaHook } = useOrdenOperations();
    const { guardarComentario } = useOrdenComentarios();
    const { token, setToken, nombreCompleto } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const navigate = useNavigate();
    const [showModalCambiarVendedor, setModalCambiarVendedor] = React.useState(false);
    const [showModalGeneratingPDF, setShowModalGeneratingPDF] = React.useState(false);
    const [showModalVincularXml, setShowModalVincularXml] = React.useState(false);
    const [showModalSuscripcion, setShowModalSuscripcion] = React.useState(false);

    const handleCambiarFecha = async (e) => {
        const nuevaFecha = e.target.value;

        await cambiarFechaHook(
            orden,
            nuevaFecha,
            (data) => {
                guardarComentario(
                    orden.idOrden,
                    `<b>${nombreCompleto}</b> cambi&oacute; la fecha de la orden de: <b>${orden.dFechaVentaLarga}</b> a la fecha: <b>${data.dFechaVentaLarga}</b>`,
                    2,
                    orden,
                    (comentarioBD) => {
                        onComentarioAgregado(comentarioBD);
                    },
                    (error) => {
                        showErrorToast(error);
                    }
                );

                setOrden(prevOrden => ({
                    ...prevOrden,
                    dFechaVenta: data.dFechaVenta,
                    dFechaVentaLarga: data.dFechaVentaLarga
                }));

                showSuccessToast("Cambio de fecha exitoso");
                setSeleccionarFecha(false);
            },
            (error) => {
                showErrorToast(error);

                setSeleccionarFecha(false);
            }
        );
    };

    const handleGenerarNotaVenta = async () => {
        try {
            // Mostrar modal de loading
            setShowModalGeneratingPDF(true);

            const jDataSend = {
                idOrden: orden.idOrden
            };

            const response = await fetch("/api/orden/generarNotaVenta", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(jDataSend)
            });

            if (response.ok) {
                // Verificar si la respuesta es un PDF
                const contentType = response.headers.get('content-type');

                if (contentType && contentType.includes('application/pdf')) {
                    // Es un PDF, descargarlo
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `NotaVenta_${orden.idOrden}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    showSuccessToast("Nota de venta generada exitosamente");

                    // Guardar comentario
                    guardarComentario(
                        orden.idOrden,
                        `<b>${nombreCompleto}</b> generó la nota de venta`,
                        2,
                        orden,
                        (comentarioBD) => {
                            if (onComentarioAgregado) {
                                onComentarioAgregado(comentarioBD);
                            }
                        }
                    );
                } else {
                    // Es JSON (posible error)
                    const objResponse = await response.json();
                    showErrorToast(objResponse.vMessage || 'Error al generar la nota de venta');
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                } else {
                    // Intentar leer el error como JSON
                    try {
                        const objResponse = await response.json();
                        showErrorToast(objResponse.vMessage || 'Error al generar la nota de venta');
                    } catch {
                        showErrorToast(`Error al generar la nota de venta (${response.status})`);
                    }
                }
            }
        } catch (error) {
            console.log('Hubo un problema con la petición de nota de venta:' + error);
            showErrorToast('Hubo un problema con la petición de nota de venta: ' + error);
        } finally {
            // Ocultar modal de loading
            setShowModalGeneratingPDF(false);
        }
    };

    const handleFacturarVenta = async () => {
        //showSuccessToast("Le di click en facturar");
        try {
            // Primero hacer las validaciones previas
            const jDataSend = {
                idOrden: orden.idOrden,
                vFormaPago: orden.vClave // Usar la forma de pago de la orden
            };

            const response = await fetch("/api/contabilidad/facturas/FormularioFactura/validacionesPreviasAfacturar", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(jDataSend)
            });
            const objResponse = await response.json();
            if (response.ok) {

                if (objResponse.iResponseType === 1) {
                    // Validación exitosa, redirigir al formulario de factura usando navigate
                    navigate(`/contabilidad/facturacion/formulario-factura/${orden.idOrden}`);
                } else {
                    // Validación fallida, mostrar mensaje de error
                    showErrorToast(objResponse.vMessage);

                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                } else {
                    showErrorToast(objResponse.vMessage || 'Error al validar la orden para facturar.');

                }
            }
        } catch (error) {
            console.log('Hubo un problema con la petición de validación:' + error);
            showErrorToast('Hubo un problema con la petición de validación: ' + error);

        }
    };

    const handleVendedorSelected = async (usuario) => {
        if (usuario) {
            try {
                const jDataSend = {
                    idOrden: orden.idOrden,
                    idVendedor: usuario.idUsuario
                };

                const response = await fetch("/api/orden/cambiarvendedor", {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(jDataSend)
                });
                const objResponse = await response.json();
                if (response.ok) {
                    if (objResponse.iResponseType === 1) {
                        // Cambio exitoso
                        setOrden(prev => ({ ...prev, idVendedor: usuario.idUsuario, vNombreVendedor: usuario.vNombreCompleto, vUsuario: usuario.vUsuario }));
                        showSuccessToast("Vendedor cambiado exitosamente");
                        setModalCambiarVendedor(false);
                    } else {
                        // Error en la respuesta
                        showErrorToast(objResponse.vMessage);
                    }
                } else {
                    if (response.status === 401) {
                        setToken(null);
                        location.href = "";
                    } else {
                        showErrorToast(objResponse.vMessage || 'Error al cambiar el vendedor.');
                    }
                }
            } catch (error) {
                console.log('Hubo un problema con la petición de cambio de vendedor:' + error);
                showErrorToast('Hubo un problema con la petición de cambio de vendedor: ' + error);
            }
        }
    };

    const handleCambiarTipoDistribuidor = async (esDistribuidor) => {
        try {
            const jDataSend = {
                idOrden: orden.idOrden.toString(),
                bIsDistribucion: esDistribuidor.toString()
            };

            const response = await fetch("/api/orden/changeDistribution", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(jDataSend)
            });
            const objResponse = await response.json();
            if (response.ok) {
                if (objResponse.iResponseType === 1) {
                    // Cambio exitoso
                    setOrden(prev => ({ ...prev, bIsDistribucion: esDistribuidor }));
                    showSuccessToast(objResponse.vMessage || "Tipo de orden cambiado exitosamente");

                    // Guardar comentario del cambio
                    const tipoNuevo = esDistribuidor ? 'Distribuidor' : 'Akasia';
                    guardarComentario(
                        orden.idOrden,
                        `<b>${nombreCompleto}</b> determinó que esta venta fue realizada por: <b>${tipoNuevo}</b>`,
                        2,
                        orden,
                        (comentarioBD) => {
                            if (onComentarioAgregado) {
                                onComentarioAgregado(comentarioBD);
                            }
                        }
                    );
                } else {
                    // Error en la respuesta
                    showErrorToast(objResponse.vMessage);
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                } else {
                    showErrorToast(objResponse.vMessage || 'Error al cambiar el tipo de orden.');
                }
            }
        } catch (error) {
            console.log('Hubo un problema con la petición de cambio de tipo:' + error);
            showErrorToast('Hubo un problema con la petición de cambio de tipo: ' + error);
        }
    };

    return (
        <Card className="mb-3 p-3">
            <Row className="g-2 align-items-sm-center">
                <Col>
                    <h5>
                        {loadingInfoDetallesOrden ? <Skeleton width={300} /> : <>
                            DETALLE DE LA ORDEN: # {orden.idOrden}

                            {/* BADGE DE SUSCRIPCIÓN */}
                            {orden.idSuscripcion && (
                                <a
                                    href={`/suscripciones/${orden.idSuscripcion}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-decoration-none ms-2"
                                    title="Ver detalles de la suscripción"
                                >
                                    <SoftBadge
                                        pill
                                        bg="info"
                                        className="fs--2"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={faArrowsRotate} className="me-1" />
                                        Suscripci&oacute;n #{orden.idSuscripcion}{orden.vNombreSuscripcion ? `: ${orden.vNombreSuscripcion}` : ''}
                                    </SoftBadge>
                                </a>
                            )}
                        </>}
                    </h5>
                </Col>
                <Col md="auto">
                    {loadingInfoDetallesOrden ? <Skeleton width={100} /> : <>
                        {orden.bIsActive &&
                            <DropdownButton className='me-2 mb-1'
                                variant="falcon-primary"
                                title="Acciones"
                                size="sm">
                                <Dropdown.Item>
                                    {loadingEdit ?
                                        <span className="text-muted fw-bold">
                                            <Spinner
                                                as="span"
                                                animation="grow"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            /> Procesando
                                        </span>
                                        :
                                        (orden.bPoderEditarOrden ? (
                                            <span className=" ms-1 text-dark" onClick={() => { verificaSiOrdenTienePagos(1); }}>
                                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                Editar Orden
                                            </span>
                                        ) : (
                                            <span className=" ms-1 text-muted" style={{ cursor: 'not-allowed' }}>
                                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                No se puede editar
                                            </span>
                                        ))
                                    }
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setShowModalVincularXml(true)}>
                                    <FontAwesomeIcon icon={faLink} className="me-2" />
                                    Vincular XML emitido
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => { showErrorToast('Por el momento no es posible enviar por e-mail'); }}>
                                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                    Enviar confirmaci&oacute;n de venta por e-mail
                                </Dropdown.Item>
                                <Dropdown.Divider as="div" />
                                <Dropdown.Item>
                                    {loadingEdit ?
                                        <span className="text-muted fw-bold">
                                            <Spinner
                                                as="span"
                                                animation="grow"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            /> Procesando
                                        </span>
                                        :
                                        (orden.bSePuedeCancelar ? (
                                            <span className=" ms-1 text-danger" onClick={() => { verificaSiOrdenTienePagos(2); }}>
                                                <FontAwesomeIcon icon={faBan} className="me-2" />
                                                Cancelar Orden
                                            </span>
                                        ) : (
                                            <span className=" ms-1 text-muted" style={{ cursor: 'not-allowed' }}>
                                                <FontAwesomeIcon icon={faBan} className="me-2" />
                                                No se puede cancelar
                                            </span>
                                        ))
                                    }
                                </Dropdown.Item>
                            </DropdownButton>
                        }
                    </>}
                </Col>
                <Col md="auto">
                    {loadingInfoDetallesOrden ? <Skeleton width={100} /> : <>
                        {orden.bIsActive &&
                            <>
                                <Button
                                    size="sm"
                                    className="me-2 mb-1"
                                    variant="falcon-success"
                                    onClick={handleGenerarNotaVenta}
                                >
                                    <FontAwesomeIcon icon="file-invoice" className="me-1" />
                                    Generar Nota de Venta
                                </Button>
                            </>
                        }
                    </>}
                </Col>
                <Col md="auto">
                    {loadingInfoDetallesOrden ? <Skeleton width={100} /> : <>
                        {orden.bIsActive && !orden.bIsFacturado &&
                            <>
                                <Button
                                    size="sm"
                                    className="me-2 mb-1"
                                    variant="falcon-primary"
                                    onClick={handleFacturarVenta}
                                >
                                    Facturar venta
                                </Button>
                            </>
                        }

                    </>}
                </Col>
                <Col md="auto">
                    <FacturaDropdown
                        orden={orden}
                        setOrden={setOrden}
                        idOrden={orden.idOrden}
                    />
                </Col>
            </Row>
            {loadingInfoDetallesOrden ? <Skeleton width={250} /> :
                <p className="fs--1 mt-1">
                    {seleccionarFecha ? <>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Fecha de Venta</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={new Date(orden.dFechaVenta).toISOString().split('T')[0]}
                                    name="dFechaVenta"
                                    onChange={handleCambiarFecha}
                                />
                            </Form.Group>
                        </Col>
                    </> :
                        <>
                            <Link to={""} onClick={() => { setSeleccionarFecha(true); }}>
                                <img src="/img/editar20.png" alt="Editar"></img>
                            </Link>
                            &nbsp;&nbsp;{orden.dFechaVentaLarga + (orden.vMoneda != 'MXN' ? "   |   T.C." + orden.dTipoCambio : "")}
                        </>
                    }
                </p>
            }
            <Row>
                <Col md={8}>
                    <div>
                        {loadingInfoDetallesOrden ? <Skeleton width={200} /> : <>
                            <strong className="me-2 fs--1">Estatus: </strong>
                            {orden.bIsDistribucion == null ?
                                <ButtonGroup className="m-1 mt-2">
                                    <ToggleButton
                                        type="radio"
                                        id="rd_1"
                                        variant={'outline-danger'}
                                        name="radioDist"
                                        value={true}
                                        checked={false}
                                        size="sm"
                                        style={{ transition: '0.3s' }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#dc3545';
                                            e.target.style.color = 'white';
                                            e.target.style.borderColor = '#dc3545';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.color = '#dc3545';
                                        }}
                                        onChange={() => handleCambiarTipoDistribuidor(true)}
                                    >
                                        Distribuidor
                                    </ToggleButton>

                                    <ToggleButton
                                        type="radio"
                                        id="rd_2"
                                        variant={'outline-primary'}
                                        name="radioDist"
                                        value={false}
                                        checked={false}
                                        size="sm"
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#0d6efd';
                                            e.target.style.color = 'white';
                                            e.target.style.borderColor = '#0d6efd';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.color = '#0d6efd';
                                        }}
                                        onChange={() => handleCambiarTipoDistribuidor(false)}
                                    >
                                        Akasia
                                    </ToggleButton>
                                </ButtonGroup>
                                :
                                <SoftBadge
                                    pill
                                    bg={orden.bIsDistribucion == true ? 'info' : 'primary'}
                                    className="fs--1 m-1"
                                >
                                    <span className="mb-0">
                                        {orden.bIsDistribucion == true ? 'Distribuidor' : 'Akasia'}
                                    </span>
                                    <FontAwesomeIcon
                                        icon={orden.bIsDistribucion == true ? faUserTie : faUser}
                                        transform="shrink-2"
                                        className="ms-1"
                                    />
                                </SoftBadge>
                            }

                            <SoftBadge
                                pill
                                bg={orden.bIsRecompra ? 'primary' : 'success'}
                                className="fs--1"
                            >
                                <span className="mb-0">
                                    {orden.bIsRecompra && 'Re-compra'}
                                    {!orden.bIsRecompra && 'Primer Compra'}
                                </span>
                                <FontAwesomeIcon
                                    icon={classNames({ check: true })}
                                    transform="shrink-2"
                                    className="ms-1"
                                />
                            </SoftBadge>

                            &nbsp;
                            {orden.bIsActive == 1 && <>
                                <SoftBadge
                                    pill
                                    bg={orden.bIsPagada ? 'success' : 'warning'}
                                    className="fs--1"
                                >
                                    <span className="mb-0">
                                        {orden.bIsPagada === true && 'Pagada'}
                                        {orden.bIsPagada === false && 'No pagada'}
                                    </span>
                                    {orden.bIsPagada !== null && orden.bIsPagada !== undefined && (
                                        <FontAwesomeIcon
                                            icon={orden.bIsPagada ? "check" : "stream"}
                                            transform="shrink-2"
                                            className="ms-1"
                                        />
                                    )}
                                </SoftBadge>
                                &nbsp;
                                {orden.dSaldoInsoluto > 0 &&
                                    <SoftBadge
                                        pill
                                        bg={'warning'}
                                        className="fs--1"
                                    >
                                        <span className="mb-0">
                                            <span>Saldo Insoluto: &nbsp;&nbsp; ${parseFloat(orden.dSaldoInsoluto).toLocaleString("es-MX", {
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2
                                            })} {orden.vMoneda}</span>
                                        </span>
                                        <FontAwesomeIcon
                                            icon={classNames({ stream: true })}
                                            transform="shrink-2"
                                            className="ms-1"
                                        />
                                    </SoftBadge>}
                            </>}
                            {orden.bIsActive == 0 &&
                                <SoftBadge
                                    pill
                                    bg="danger"
                                    className="fs--1"
                                >
                                    <span className="mb-0">Cancelada</span>
                                    <FontAwesomeIcon
                                        icon="ban"
                                        transform="shrink-2"
                                        className="ms-1"
                                    />
                                </SoftBadge>}
                        </>}
                    </div>
                    <div>
                        {loadingInfoDetallesOrden ? <Skeleton width={200} /> : <>
                            <strong className="me-2 fs--1 text-center">Vendedor:&nbsp;&nbsp;</strong>
                            <strong className="me-2 fs--2 text-center">
                                <img height="20" src="/img/vendedor35.png" alt="Vendedor"></img> {orden.vUsuario} - {orden.vNombreVendedor}
                            </strong>
                            <Link to={""} onClick={() => { setModalCambiarVendedor(true); }}>
                                <img src="/img/editar20.png" alt="Editar"></img>
                            </Link>

                        </>}
                    </div>
                    <div>
                        {loadingInfoDetallesOrden ? <Skeleton width={200} /> : <>
                            {orden.bIsDistribucion && <>
                                <strong className="me-2 fs--1 text-center">Distribuidor:&nbsp;&nbsp;</strong>
                                <Link to={"/clientes/detalle/" + orden.idDistribuidor}>
                                    <strong className="me-2 fs--2 text-center"> {orden.idDistribuidor} - {orden.vNombreDistribuidor}</strong>
                                </Link>
                            </>}
                        </>}
                    </div>
                </Col>
                <OrdenFormaPago
                    loadingInfoDetallesOrden={loadingInfoDetallesOrden}
                    orden={orden}
                    setOrden={setOrden}
                    seleccionarFormaPago={seleccionarFormaPago}
                    setSeleccionarFormaPago={setSeleccionarFormaPago}
                    nuevaFormaPago={nuevaFormaPago}
                    setNuevaFormaPago={setNuevaFormaPago}
                    onComentarioAgregado={onComentarioAgregado}
                />
            </Row>

            <ModalCambiarVendedor
                show={showModalCambiarVendedor}
                onHide={() => setModalCambiarVendedor(false)}
                onSelect={handleVendedorSelected}
                currentVendedorId={orden.idVendedor}
            />

            <ModalVincularXmlEmitido
                show={showModalVincularXml}
                onHide={() => setShowModalVincularXml(false)}
                idOrden={orden?.idOrden || 0}
                onLinked={(data) => {
                    // cuando se vincula, refrescar la orden o mostrar toast
                    if (data && data.iResponseType === 1) {
                        // Puede que el servidor retorne info adicional; mostrar toast ya hecho en modal
                        // refrescar página o detalles si es necesario
                        // setOrden(prev => ({ ...prev }));
                    }
                }}
            />

            <ModalGeneratingPDF
                show={showModalGeneratingPDF}
                title="Generando Nota de Venta"
                message="Por favor espere mientras se genera el documento PDF..."
            />

            <ModalDetalleSuscripcion
                show={showModalSuscripcion}
                onHide={() => setShowModalSuscripcion(false)}
                idSuscripcion={orden.idSuscripcion}
            />
        </Card>
    );
};

SeccionHeader.propTypes = {
    loadingInfoDetallesOrden: PropTypes.bool.isRequired,
    orden: PropTypes.object.isRequired,
    setOrden: PropTypes.func.isRequired,
    loadingEdit: PropTypes.bool.isRequired,
    verificaSiOrdenTienePagos: PropTypes.func.isRequired,
    seleccionarFormaPago: PropTypes.bool.isRequired,
    setSeleccionarFormaPago: PropTypes.func.isRequired,
    nuevaFormaPago: PropTypes.string.isRequired,
    setNuevaFormaPago: PropTypes.func.isRequired,
    seleccionarFecha: PropTypes.bool.isRequired,
    setSeleccionarFecha: PropTypes.func.isRequired,
    onComentarioAgregado: PropTypes.func
};

export default SeccionHeader;
